import { z } from 'zod'
import { tAny } from '@/i18n'
import type { SubmitPayload } from './api'
import type { PublicAdmissions, RequiredDocument } from './types'

/** Steps of the public form (the guardians step is skipped when the school works without guardians). */
export type StepKey = 'class' | 'student' | 'guardians' | 'documents' | 'review'

export const STEP_LABELS: Record<StepKey, string> = {
  class: 'Class',
  student: 'Student',
  guardians: 'Parents',
  documents: 'Documents',
  review: 'Review',
}

export function stepsFor(info: Pick<PublicAdmissions, 'guardiansEnabled'>): StepKey[] {
  return info.guardiansEnabled
    ? ['class', 'student', 'guardians', 'documents', 'review']
    : ['class', 'student', 'documents', 'review']
}

const optional = (max: number) => z.string().trim().max(max)
const optionalEmail = z
  .string()
  .trim()
  .max(180)
  .refine((v) => v === '' || z.email().safeParse(v).success, 'Enter a valid email address.')

const guardianSchema = z.object({
  relation: z.string().min(1, 'Choose a relation.'),
  name: z.string().trim().min(1, 'Enter the name.').max(150),
  phone: optional(30),
  email: optionalEmail,
  occupation: optional(100),
  isPrimary: z.boolean(),
})

function today(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** The whole form; each step validates its own fields (see STEP_FIELDS). */
export function applicationSchema(guardiansEnabled: boolean) {
  const guardians = z.array(guardianSchema).max(3)
  return z.object({
    classId: z.string().min(1, 'Choose a class.'),
    student: z.object({
      firstName: z.string().trim().min(1, 'Enter the first name.').max(80),
      lastName: z.string().trim().min(1, 'Enter the last name.').max(80),
      gender: z.string().min(1, 'Choose the gender.'),
      dateOfBirth: z
        .string()
        .min(1, 'Enter the date of birth.')
        .refine((v) => v === '' || v < today(), 'Date of birth must be in the past.'),
      placeOfBirth: z.string().trim().min(1, 'Enter the place of birth.').max(120),
      nationality: z.string().trim().min(1, 'Enter the nationality.').max(50),
      address: z.string().trim().min(1, 'Enter the address.').max(500),
      // Without guardians, the student is the one the school calls.
      phone: guardiansEnabled
        ? optional(30)
        : optional(30).min(1, 'Give a phone number where we can reach you.'),
      email: optionalEmail,
      previousSchool: optional(150),
      previousClass: optional(100),
    }),
    guardians: guardiansEnabled
      ? guardians
          .min(1, 'Add at least one parent or guardian.')
          .refine(
            (list) => list.some((g) => g.phone.trim() !== ''),
            'Give a phone number for at least one parent or guardian.',
          )
      : guardians,
    consent: z.boolean(),
  })
}

export type ApplicationForm = z.infer<ReturnType<typeof applicationSchema>>

export const STEP_FIELDS: Record<StepKey, string[]> = {
  class: ['classId'],
  student: [
    'student.firstName',
    'student.lastName',
    'student.gender',
    'student.dateOfBirth',
    'student.placeOfBirth',
    'student.nationality',
    'student.address',
    'student.phone',
    'student.email',
  ],
  guardians: ['guardians'],
  documents: ['student.previousSchool', 'student.previousClass'],
  review: [],
}

/** Which step shows a field reported by the server ("student.firstName", "guardians[0].phone"…). */
export function stepOfField(field: string): StepKey {
  if (field === 'classId') return 'class'
  if (field.startsWith('guardians')) return 'guardians'
  if (field.startsWith('documents') || field.startsWith('requiredDocuments')) return 'documents'
  if (field === 'student.previousSchool' || field === 'student.previousClass') return 'documents'
  if (field.startsWith('student')) return 'student'
  return 'review'
}

export const emptyGuardian = (isPrimary = false): ApplicationForm['guardians'][number] => ({
  relation: '',
  name: '',
  phone: '',
  email: '',
  occupation: '',
  isPrimary,
})

export function emptyApplication(guardiansEnabled: boolean): ApplicationForm {
  return {
    classId: '',
    student: {
      firstName: '',
      lastName: '',
      gender: '',
      dateOfBirth: '',
      placeOfBirth: '',
      nationality: '',
      address: '',
      phone: '',
      email: '',
      previousSchool: '',
      previousClass: '',
    },
    guardians: guardiansEnabled ? [emptyGuardian(true)] : [],
    consent: false,
  }
}

/** A document sent to the server, kept by required-document index. */
export interface UploadedDoc {
  token: string
  name: string
  size: number
  expiresAt: string
}
export type UploadedDocs = Record<number, UploadedDoc>

/** Required uploads still missing. */
export function missingDocuments(required: RequiredDocument[], docs: UploadedDocs): RequiredDocument[] {
  return required.filter((d) => d.upload && d.required && !docs[d.index])
}

export function buildPayload(values: ApplicationForm, docs: UploadedDocs, honeypot: string): SubmitPayload {
  const blank = (v: string) => (v.trim() === '' ? null : v.trim())
  const s = values.student
  return {
    classId: Number(values.classId),
    student: {
      firstName: s.firstName.trim(),
      lastName: s.lastName.trim(),
      gender: s.gender,
      dateOfBirth: s.dateOfBirth,
      placeOfBirth: s.placeOfBirth.trim(),
      nationality: s.nationality.trim(),
      address: s.address.trim(),
      phone: blank(s.phone),
      email: blank(s.email),
      previousSchool: blank(s.previousSchool),
      previousClass: blank(s.previousClass),
    },
    guardians: values.guardians.map((g) => ({
      relation: g.relation,
      name: g.name.trim(),
      phone: blank(g.phone),
      email: blank(g.email),
      occupation: blank(g.occupation),
      isPrimary: g.isPrimary,
    })),
    documents: Object.entries(docs).map(([index, d]) => ({ index: Number(index), uploadToken: d.token })),
    consent: values.consent,
    website: honeypot,
  }
}

/* Draft kept in this browser so a refresh or a lost connection does not lose the form. */

export interface Draft {
  step: StepKey
  values: ApplicationForm
  docs: UploadedDocs
  /** Year the draft was written for: a draft for another intake is dropped. */
  yearId: number | null
}

const draftKey = () => `schooleduca.admission-draft:${window.location.host}`

export function loadDraft(yearId: number | null): Draft | null {
  try {
    const raw = window.localStorage.getItem(draftKey())
    if (!raw) return null
    const draft = JSON.parse(raw) as Draft
    if (draft.yearId !== yearId || typeof draft.values !== 'object') return null
    // Uploads are kept a day on the server: expired ones must be sent again.
    const now = Date.now()
    const docs: UploadedDocs = {}
    for (const [k, d] of Object.entries(draft.docs)) {
      if (new Date(d.expiresAt).getTime() > now + 5 * 60_000) docs[Number(k)] = d
    }
    return { ...draft, docs }
  } catch {
    return null
  }
}

export function saveDraft(draft: Draft): void {
  try {
    window.localStorage.setItem(draftKey(), JSON.stringify(draft))
  } catch {
    // Private mode or full storage: the form still works, without a draft.
  }
}

export function clearDraft(): void {
  try {
    window.localStorage.removeItem(draftKey())
  } catch {
    // ignore
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return tAny('{{value}} KB', { value: Math.max(1, Math.round(bytes / 1024)) })
  return tAny('{{value}} MB', { value: (bytes / 1024 / 1024).toFixed(1) })
}
